import { supabase } from '@/integrations/supabase/client';
import { isSupabaseMode } from '@/config/dataSource';
import {
  FluxoCaixaEvolucaoMensal,
  FluxoCaixaFiltros,
  FluxoCaixaFormData,
  FluxoCaixaLancamento,
  FluxoCaixaResumo,
} from '../types/fluxoCaixa';
import { agruparPorMes } from '../utils/fluxoCaixaCalculations';
import dayjs from 'dayjs';

export class FluxoCaixaService {
  async listar(tenantId: number, filtros: FluxoCaixaFiltros): Promise<FluxoCaixaLancamento[]> {
    if (!isSupabaseMode()) {
      throw new Error('Apenas Supabase é suportado para Fluxo de Caixa');
    }

    let query = supabase
      .from('fluxo_caixa')
      .select('*')
      .eq('tenant_id', tenantId)
      .gte('data_lancamento', filtros.dataInicio)
      .lte('data_lancamento', filtros.dataFim)
      .order('data_lancamento', { ascending: false });

    if (filtros.tipo) {
      query = query.eq('lancamento_tipo', filtros.tipo);
    }

    if (filtros.categorias && filtros.categorias.length > 0) {
      query = query.in('categoria', filtros.categorias);
    }

    if (filtros.centrosCusto && filtros.centrosCusto.length > 0) {
      query = query.in('centro_custo', filtros.centrosCusto);
    }

    const { data, error } = await query;

    if (error) throw error;

    return (data || []).map(row => ({
      ...row,
      lancamento_tipo: row.lancamento_tipo as FluxoCaixaLancamento['lancamento_tipo'],
      categoria: row.categoria as FluxoCaixaLancamento['categoria'],
      centro_custo: row.centro_custo as FluxoCaixaLancamento['centro_custo'],
      valor: Number(row.valor),
    }));
  }

  async obterResumo(
    tenantId: number,
    dataInicio: string,
    dataFim: string
  ): Promise<FluxoCaixaResumo> {
    if (!isSupabaseMode()) {
      throw new Error('Apenas Supabase é suportado para Fluxo de Caixa');
    }

    const { data, error } = await supabase.rpc('get_fluxo_caixa_resumo', {
      p_tenant_id: tenantId,
      p_data_inicio: dataInicio,
      p_data_fim: dataFim,
    });

    if (error) throw error;

    const row = Array.isArray(data) ? data[0] : data;

    return {
      total_entrada: Number(row?.total_entrada ?? 0),
      total_saida: Number(row?.total_saida ?? 0),
      saldo: Number(row?.saldo ?? 0),
    };
  }

  async criar(
    tenantId: number,
    userId: string,
    formData: FluxoCaixaFormData
  ): Promise<FluxoCaixaLancamento> {
    const { data, error } = await supabase
      .from('fluxo_caixa')
      .insert({
        tenant_id: tenantId,
        lancamento_tipo: formData.lancamento_tipo,
        categoria: formData.categoria,
        centro_custo: formData.centro_custo || null,
        valor: formData.valor,
        descricao: formData.descricao || null,
        documento: formData.documento || null,
        data_lancamento: formData.data_lancamento,
        lancado_por: userId,
      })
      .select('*')
      .single();

    if (error) throw error;

    return {
      ...data,
      lancamento_tipo: data.lancamento_tipo as FluxoCaixaLancamento['lancamento_tipo'],
      categoria: data.categoria as FluxoCaixaLancamento['categoria'],
      centro_custo: data.centro_custo as FluxoCaixaLancamento['centro_custo'],
      valor: Number(data.valor),
    };
  }

  async atualizar(
    id: number,
    formData: Partial<FluxoCaixaFormData>
  ): Promise<FluxoCaixaLancamento> {
    const { data, error } = await supabase
      .from('fluxo_caixa')
      .update({
        ...(formData.categoria !== undefined && { categoria: formData.categoria }),
        ...(formData.centro_custo !== undefined && { centro_custo: formData.centro_custo || null }),
        ...(formData.valor !== undefined && { valor: formData.valor }),
        ...(formData.descricao !== undefined && { descricao: formData.descricao || null }),
        ...(formData.documento !== undefined && { documento: formData.documento || null }),
        ...(formData.data_lancamento !== undefined && { data_lancamento: formData.data_lancamento }),
      })
      .eq('id', id)
      .select('*')
      .single();

    if (error) throw error;

    return {
      ...data,
      lancamento_tipo: data.lancamento_tipo as FluxoCaixaLancamento['lancamento_tipo'],
      categoria: data.categoria as FluxoCaixaLancamento['categoria'],
      centro_custo: data.centro_custo as FluxoCaixaLancamento['centro_custo'],
      valor: Number(data.valor),
    };
  }

  async deletar(id: number): Promise<void> {
    const { error } = await supabase.from('fluxo_caixa').delete().eq('id', id);

    if (error) throw error;
  }

  async obterEvolucaoMensal(tenantId: number, meses = 3): Promise<FluxoCaixaEvolucaoMensal[]> {
    const fim = dayjs();
    const inicio = fim.subtract(meses - 1, 'month').startOf('month');

    const lancamentos = await this.listar(tenantId, {
      dataInicio: inicio.format('YYYY-MM-DD'),
      dataFim: fim.format('YYYY-MM-DD'),
      tipo: null,
      categorias: [],
      centrosCusto: [],
    });

    return agruparPorMes(lancamentos, meses);
  }
}

export const fluxoCaixaService = new FluxoCaixaService();
